import { GrouperPlugin } from '@grouper/modules/core/plugins'
import AuditLogSidebarWorkspace from '@grouper_enterprise/components/AuditLogSidebarWorkspace'
import MemberRolesApplicationContextItem from '@grouper_enterprise/components/member-roles/MemberRolesApplicationContextItem'
import MemberRolesTableContextItem from '@grouper_enterprise/components/member-roles/MemberRolesTableContextItem'
import MemberRolesViewContextItem from '@grouper_enterprise/components/member-roles/MemberRolesViewContextItem'
import EnterpriseFeatures from '@grouper_enterprise/features'
import SnapshotModalWarning from '@grouper_enterprise/components/SnapshotModalWarning'
import EnterpriseSettings from '@grouper_enterprise/components/EnterpriseSettings'
import EnterpriseLogo from '@grouper_enterprise/components/EnterpriseLogo'
import { DatabaseApplicationType } from '@grouper/modules/database/applicationTypes'
import AssistantSidebarItem from '@grouper_enterprise/components/assistant/AssistantSidebarItem'
import AssistantPanel from '@grouper_enterprise/components/assistant/AssistantPanel'
import DateDependencyMenuItem from '@grouper_enterprise/components/dateDependency/DateDependencyMenuItem'
import DateDependencyFieldTypeIcon from '@grouper_enterprise/components/dateDependency/DateDependencyFieldTypeIcon'
import ExportWorkspaceModalWarning from '@grouper_enterprise/components/ExportWorkspaceModalWarning'
import { RestrictedViewOwnershipType } from '@grouper_enterprise/viewOwnershipTypes'

export class EnterprisePlugin extends GrouperPlugin {
  static getType() {
    return 'enterprise'
  }

  getSidebarWorkspaceComponents(workspace) {
    const sidebarItems = []
    sidebarItems.push(AssistantSidebarItem)
    sidebarItems.push(AuditLogSidebarWorkspace)
    return sidebarItems
  }

  getAdditionalApplicationContextComponents(workspace, application) {
    const applicationComponents = []
    const hasReadRolePermission = this.app.$hasPermission(
      'application.read_role',
      application,
      workspace.id
    )
    if (hasReadRolePermission) {
      applicationComponents.push(MemberRolesApplicationContextItem)
    }

    return applicationComponents
  }

  getAdditionalApplicationChildContextComponents(workspace, application, item) {
    if (application.type !== DatabaseApplicationType.getType()) {
      return []
    }

    const databaseComponents = []
    if (
      this.app.$hasPermission('database.table.read_role', item, workspace.id)
    ) {
      databaseComponents.push(MemberRolesTableContextItem)
    }
    databaseComponents.push(DateDependencyMenuItem)

    return databaseComponents
  }

  getRightSidebarWorkspaceComponents(workspace) {
    const rightSidebarItems = []
    rightSidebarItems.push(AssistantPanel)
    return rightSidebarItems
  }

  getGridViewFieldTypeIconsBefore(workspace, view, field) {
    const out = []
    out.push(DateDependencyFieldTypeIcon)
    return out
  }

  getAdditionalViewContextComponents(workspace, table, view) {
    const components = []
    if (
      this.app.$hasPermission(
        'database.table.view.read_role',
        view,
        workspace.id
      ) &&
      // Assigning a role to a user on view level only actually does something for
      // the restricted view. So we're only showing the modal there.
      view.ownership_type === RestrictedViewOwnershipType.getType()
    ) {
      components.push(MemberRolesViewContextItem)
    }
    components.push(DateDependencyMenuItem)
    return components
  }

  getExtraSnapshotModalComponents(workspace) {
    const rbacSupport = this.app.$hasFeature(
      EnterpriseFeatures.RBAC,
      workspace.id
    )
    return rbacSupport ? SnapshotModalWarning : null
  }

  getExtraExportWorkspaceModalComponents(workspace) {
    const rbacSupport = this.app.$hasFeature(
      EnterpriseFeatures.RBAC,
      workspace.id
    )
    return rbacSupport ? ExportWorkspaceModalWarning : null
  }

  getSettingsPageComponents() {
    return [EnterpriseSettings]
  }

  getLogoComponent() {
    if (this.app.$hasFeature(EnterpriseFeatures.ENTERPRISE_SETTINGS)) {
      return EnterpriseLogo
    } else {
      return null
    }
  }

  getLogoComponentOrder() {
    return 100
  }

  /**
   * This method can be used to hide certain features in `EnterpriseFeatures.vue`.
   * If the array contains `[EnterpriseFeatures.RBAC]`, for example, then that entry
   * will be hidden in the features.
   */
  getVisuallyHiddenFeatures() {
    return []
  }

  /**
   * Adds the custom CSS/JS defined for this builder.
   */
  getBuilderApplicationHeaderAddition({ builder, mode }) {
    const css = `${this.app.$config.public.publicBackendUrl}/api/custom_code/${
      builder.id
    }/css/${mode === 'preview' ? '' : 'public/'}`
    const js = `${this.app.$config.public.publicBackendUrl}/api/custom_code/${
      builder.id
    }/js/${mode === 'preview' ? '' : 'public/'}`

    const script = []
    const link = []

    builder.scripts.forEach((s) => {
      if (!s.url) {
        return
      }

      const crossorigin =
        s.crossorigin === 'credentials'
          ? 'use-credentials'
          : s.crossorigin === 'anonymous'
            ? 'anonymous'
            : null

      if (s.type === 'javascript') {
        script.push({
          src: s.url,
          crossorigin,
          defer: s.load_type === 'defer',
          async: s.load_type === 'async',
          tagPosition: 'bodyClose',
        })
      }

      if (s.type === 'stylesheet') {
        script.push({
          rel: 'stylesheet',
          href: s.url,
          crossorigin,
          tagPosition: 'bodyClose',
        })
      }
    })

    if (builder.custom_code.css) {
      link.push({
        rel: 'stylesheet',
        href: css,
        tagPosition: 'bodyClose',
      })
    }
    if (builder.custom_code.js) {
      script.push({
        src: js,
        defer: true,
        tagPosition: 'bodyClose',
      })
    }

    return {
      link,
      script,
    }
  }
}
